/**
 * Which "มีอะไรใหม่" entry is the newest, for the app shell's unread dot — the shell must not
 * download the changelog for that.
 *
 * An entry is known by its place in help/changelog.md, counted from the oldest: the first entry
 * ever written is n0001, the next n0002, … The person's marker (`whatsNewSeen`) is the id of the
 * newest entry they have opened, and "unread" is simply "a higher number exists". Dates are only
 * what the page shows: several entries can share a day, and an entry never needs a date in the
 * future to count as new.
 *
 * To add an entry: put it at the TOP of changelog.md with today's date and add one to
 * WHATS_NEW_COUNT. apps/web/test/helpContent.test.ts fails when the two disagree.
 */
export const WHATS_NEW_COUNT = 56;

export const whatsNewId = (seq: number): string => `n${String(seq).padStart(4, '0')}`;

export const WHATS_NEW_LATEST = whatsNewId(WHATS_NEW_COUNT);

/**
 * Before the ids, the marker was the DATE of the newest entry seen. Those markers are still in
 * people's saved state: each date stands for the last entry that carried it then. Frozen — entries
 * written after the change never appear here. (Two of them were dated a day or two ahead so the dot
 * would show; that is what the ids replace.)
 */
const LEGACY_DATES: readonly [date: string, seq: number][] = [
  ['2026-09-26', 1],
  ['2026-09-27', 2],
  ['2026-09-29', 3],
  ['2026-10-01', 7],
  ['2026-10-02', 12],
  ['2026-10-04', 15],
  ['2026-10-05', 17],
  ['2026-10-06', 18],
  ['2026-10-07', 19],
];

/** The number of the newest entry a marker covers: 0 for none, an id's own number, or what a legacy date stood for. */
export function whatsNewSeq(marker: string | null | undefined): number {
  if (!marker) return 0;
  const id = /^n(\d{4})$/.exec(marker);
  if (id) return Number(id[1]);
  let seq = 0;
  for (const [date, n] of LEGACY_DATES) if (date <= marker) seq = n;
  return seq;
}

export const whatsNewUnread = (marker: string | null | undefined): boolean => whatsNewSeq(marker) < WHATS_NEW_COUNT;
